// ============================================================
// DisplayScene - the display: the tactical map drawn as a hololith.
//
// A sibling of BattlefieldScene (ADR 2026-10-07): the same props (its own
// type, so the two cannot drift apart), the same fixed 1000x700 viewBox,
// the same floor and the same hit-test attributes on every face, so the
// panel's gestures, layers and hit tests work on either. What differs is the
// grammar (engine/display/grammar): faces filled by their material's
// transparency and nothing else, edges drawn apart as pieces (a seam between
// two solids of one surface is not a line) and as their material is, the
// edges the eye cannot see dashed and faint. The children - markers, tape,
// templates - are drawn over the terrain, as on the classic map.
//
// The face and edge layers are memoised on their own inputs: the panel
// re-renders on every hover and drag, and the terrain has not changed.
// ============================================================
import { surfaceLabels, surfacesOf } from '../../engine/display/surfaces';
import { AmbientFloor, AmbientFrame } from './Ambient';
import { memo, useContext, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import useBriefly from '../../hooks/useBriefly';
import useMapGpu from '../../hooks/useMapGpu';
import GpuCanvas from './gpu/GpuCanvas';
import { buildScene, sightTo } from '../../engine/battlefield/scene';
import type { SceneFace } from '../../engine/battlefield/scene';
import { SCENE_VIEW, makeCamera, project, projectSegment } from '../../engine/battlefield/projection';
import { gridFadeCircle } from '../../engine/battlefield/grid';
import type { Camera } from '../../engine/battlefield/projection';
import type { Solid, Vec3 } from '../../engine/battlefield/types';
import { fadeAt } from '../../engine/display/focus';
import { edgesFacing, faceLook, outlineSurfaces, treatmentOf } from '../../engine/display/grammar';
import { faceHider } from '../../engine/display/occlusion';
import type { Hider } from '../../engine/display/occlusion';
import { worldHider } from '../../engine/display/gpu/worldOcclusion';
import { solidTransparency } from '../../engine/battlefield/solids';
import { figureSamples } from '../../engine/display/figures';
import type { Outline, OutlineEdge } from '../../engine/display/grammar';
import { faceHitAttrs, toPoints } from '../battlefieldScene/sceneDrawing';
import { arrivalClasses, useArrivalLedger } from '../battlefieldScene/SceneFaces';
import type { TerrainAnimation } from '../../engine/battlefield/terrainAnimation';
import { SceneFloor } from '../battlefieldScene/SceneFloor';
import type { BattlefieldSceneProps } from '../BattlefieldScene';
import clsx from '../../utils/clsx';
import styles from '../../styles/Display.module.css';
import {
  DisplayActionContext,
  DisplayOccluderContext,
  DisplayRiseContext,
  DisplayVeilContext,
  GpuReachContext,
  GpuWedgesContext,
  makeReachStore,
  makeWedgeStore,
  type ReachStore,
  type WedgeStore,
  type XrayFigure,
} from './displayFaces';
import useDisplayCut from '../../hooks/useDisplayCut';
import useHoldsStill from '../../hooks/useHoldsStill';

/** A material's own colour, handed to the stylesheet as the tint. */
const tinted = (colour?: string): CSSProperties | undefined =>
  colour ? ({ '--tint': colour } as CSSProperties) : undefined;

/**
 * The arrivals in the display's grammar (plan map-display, F; the Terrain study): the same five and the same
 * wave as the classic map (`arrivalDelays`, the ledger in SceneFaces), but the edges are light and are there at
 * once, so it is the FILL that arrives - wiped up, risen, opened out, faded in, or found like a signal.
 */
const ARRIVE = arrivalClasses(styles);

/** A face's own style: its tint, and while arrivals are on, the delay it mounted with. */
function faceStyle(colour: string | undefined, delay: number | null): CSSProperties | undefined {
  if (delay === null) return tinted(colour);
  return { ...tinted(colour), '--sweep': `${delay}ms` } as CSSProperties;
}

const DisplayFaces = memo(function DisplayFaces({
  faces,
  selectedSolidId,
  xray,
  fades,
  animation,
}: {
  faces: SceneFace[];
  selectedSolidId: string | null;
  xray: ReadonlySet<string>;
  fades: ReadonlyMap<string, number> | null;
  animation: TerrainAnimation;
}) {
  const arrived = useArrivalLedger(faces, animation);
  const animating = animation !== 'none';
  return faces.map((face) => {
    const xrayed = xray.has(face.solidId);
    const fill = faceLook(face.kind, treatmentOf(face.transparency)).fill * (fades?.get(face.solidId) ?? 1);
    return (
      <polygon
        key={face.key}
        className={clsx(
          styles.face,
          face.motion && styles[`moves-${face.motion}`],
          face.blocked && styles.blocked,
          animating && styles.arriving,
          ARRIVE[animation],
          face.solidId === selectedSolidId && styles.selected,
          xrayed && styles.xray,
        )}
        points={toPoints(face)}
        data-xray={xrayed || undefined}
        // Painted ground's motion (zones.ts `surfaceMotion`): looks only.
        data-surface-motion={face.motion}
        data-blocked={face.blocked || undefined}
        fillOpacity={xrayed ? 0 : fill}
        style={faceStyle(face.colour, animating ? (arrived.get(face.key) ?? 0) : null)}
        {...faceHitAttrs(face)}
      >
        {face.label ? <title>{face.label}</title> : null}
      </polygon>
    );
  });
});

/**
 * Each painted surface's label on the ground (plan design-gaps, T3; the terrain page): one per region, in its own
 * colour, read at a glance where its face's <title> needed a hover.
 */
const SurfaceLabels = memo(function SurfaceLabels({
  solids,
  includeHidden,
  camera,
}: {
  solids: readonly Solid[];
  includeHidden: boolean;
  camera: Camera;
}) {
  const labels = useMemo(() => surfaceLabels(surfacesOf(solids, includeHidden)), [solids, includeHidden]);
  return labels.map(({ label, colour, at }) => {
    const p = project(at, camera, SCENE_VIEW);
    // Behind the eye a label has only a stand-in pixel: it is not drawn (the near-plane fix, 2026-10-28).
    if (p.behind) return null;
    return (
      <text
        key={label}
        data-testid="surface-label"
        className={styles.surfaceLabel}
        x={p.x}
        y={p.y + 12}
        textAnchor="middle"
        style={colour ? { fill: colour } : undefined}
      >
        {label}
      </text>
    );
  });
});

const DisplayEdges = memo(function DisplayEdges({
  edges,
  camera,
  action,
}: {
  edges: OutlineEdge[];
  camera: Camera;
  action: Vec3 | null;
}) {
  // Projected once per edge list and camera, not on every render of the panel; the fade only per terrain and action.
  const fades = useMemo(() => (action ? edges.map((e) => fadeAt(midpoint(e), action)) : null), [edges, action]);
  // Cut at the eye (`projectSegment`): up close an edge's far end can lie behind the camera, and drawn from its
  // stand-in pixel it swung across the whole screen - the blinking the table saw (2026-10-28). Keyed by the edge's
  // place in the list, so an edge that steps behind the eye and back is the same line, not a new one.
  const drawn = useMemo(
    () => edges.map((e, i) => ({ e, i, s: projectSegment(e.a, e.b, camera, SCENE_VIEW) })).filter((d) => d.s),
    [edges, camera],
  );
  return drawn.map(({ e, i, s }) => <EdgeLine key={i} edge={e} a={s!.a} b={s!.b} fade={fades?.[i] ?? 1} />);
});

/**
 * One terrain edge: a lit one drawn as its material is (stone solid, foliage
 * dotted, glass thin) and as strong as its distance from the action allows;
 * a hidden one dashed and faint.
 */
function EdgeLine({ edge, a, b, fade }: { edge: OutlineEdge; a: ScreenXY; b: ScreenXY; fade: number }) {
  const look = edge.front ? faceLook('side', edge.treatment) : null;
  return (
    <line
      className={edge.front ? styles.edge : styles.edgeHidden}
      x1={a.x}
      y1={a.y}
      x2={b.x}
      y2={b.y}
      strokeWidth={look?.width}
      strokeOpacity={look ? look.stroke * fade : undefined}
      strokeDasharray={look?.dash ?? undefined}
      style={tinted(edge.colour)}
      data-edge={edge.front ? 'front' : 'back'}
      // The hover readout, Build's selection and deploying onto a rooftop all read the FACE under the pointer.
      pointerEvents="none"
    />
  );
}

type ScreenXY = { x: number; y: number };

const NO_XRAY: ReadonlySet<string> = new Set();
/** The svg draws no terrain while the GPU canvas does: stable empties, so nothing below re-renders for them. */
const NO_FACES: SceneFace[] = [];
const NO_EDGES: OutlineEdge[] = [];

/**
 * STRIKE (plan map-display, I): the set's outline flashes once, drawn over it and gone when its flash ends - the
 * set itself is not touched, so nothing re-rises and nothing keeps a filter.
 */
function StrikeFlash({ struck, edges, camera }: { struck: number | null; edges: OutlineEdge[]; camera: Camera }) {
  const [doneFor, setDoneFor] = useState<number | null>(null);
  // Held still there is no flash: and with no animation to end, an overlay would never go.
  const still = useHoldsStill();
  if (!struck || still || doneFor === struck) return null;
  return (
    <g
      key={struck}
      className={styles.strikeFlash}
      data-struck="true"
      pointerEvents="none"
      onAnimationEnd={() => setDoneFor(struck)}
    >
      <DisplayEdges edges={edges} camera={camera} action={null} />
    </g>
  );
}

/** Two sets with the same members. */
const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>) => a.size === b.size && [...a].every((x) => b.has(x));

/**
 * The solids hiding the action's figures, drawn as wireframes so it is never
 * lost: each figure's samples from feet to head (as the see-through figures
 * are tested), with the same hysteresis, remembered from the last frame. The
 * same set comes back while its members do not change, so a renderer keyed on
 * it (the GPU map's buffers) rebuilds only when the x-ray really changes.
 */
function useXray(figures: readonly XrayFigure[] | null, hider: Hider | null): ReadonlySet<string> {
  const was = useRef<ReadonlySet<string>>(NO_XRAY);
  return useMemo(() => {
    const samples = (figures ?? []).map((f) => figureSamples(f));
    const next = samples.length && hider ? hider.xray(was.current, samples) : NO_XRAY;
    if (!sameSet(next, was.current)) was.current = next;
    return was.current;
  }, [figures, hider]);
}

/**
 * What can hide a figure this frame (engine/display/occlusion `Hider`): on the svg, its projected opaque faces and
 * its translucent ones; while the GPU canvas draws, the solids themselves (nothing is projected for it).
 */
function useHiders(faces: SceneFace[], visible: Solid[], camera: Camera, drawing: boolean) {
  // The GPU map's hiders ask the solids, split by what they do to a figure; the svg's ask its faces.
  const byTreatment = useMemo(
    () => (drawing ? { opaque: byTransparency(visible, 0), translucent: byTransparency(visible, 1) } : null),
    [drawing, visible],
  );
  return useMemo(() => {
    if (byTreatment) {
      const { opaque, translucent } = byTreatment;
      return { occluder: worldHider(opaque, camera, SCENE_VIEW), veil: worldHider(translucent, camera, SCENE_VIEW) };
    }
    const of = (t: number) => faces.filter((f) => f.transparency === t);
    return { occluder: faceHider(of(0), camera, SCENE_VIEW), veil: faceHider(of(1), camera, SCENE_VIEW) };
  }, [byTreatment, faces, camera]);
}

const byTransparency = (solids: Solid[], t: number) => solids.filter((s) => solidTransparency(s) === t);

/**
 * The svg's lit and hidden edges this frame; none while the GPU canvas draws them. Under the parallel camera only
 * the angles decide which edges face the eye, so a pan or a zoom keeps them; under perspective every move does.
 */
function useSvgEdges(outline: Outline, camera: Camera, drawing: boolean): OutlineEdge[] {
  const { yaw, pitch } = camera;
  const seeing = camera.fov ? camera : null;
  return useMemo(
    () => (drawing ? NO_EDGES : edgesFacing(outline, sightTo(seeing ?? makeCamera({ yaw, pitch }), SCENE_VIEW))),
    [drawing, outline, seeing, yaw, pitch],
  );
}

/**
 * The terrain as this frame draws it, and what can hide a figure. On the svg: the faces projected and sorted, the
 * edges lit. While the GPU canvas draws it (perf overhaul 2b) none of that is projected here - faces and edges are
 * empty and the hiders ask the solids - so a camera frame costs the svg nothing for the terrain.
 */
function useTerrainFrame(solids: Solid[], camera: Camera, includeHidden: boolean, drawing: boolean) {
  const visible = useMemo(() => (includeHidden ? solids : solids.filter((s) => !s.hidden)), [solids, includeHidden]);
  // The outline depends on the terrain only: built once per terrain, for the svg's edges or the canvas's.
  const outline = useMemo(() => outlineSurfaces(visible), [visible]);
  const faces = useMemo(
    () => (drawing ? NO_FACES : buildScene(solids, camera, SCENE_VIEW, { includeHidden })),
    [drawing, solids, camera, includeHidden],
  );
  const edges = useSvgEdges(outline, camera, drawing);
  return { visible, outline, faces, edges, ...useHiders(faces, visible, camera, drawing) };
}

const midpoint = (e: OutlineEdge) => ({ x: (e.a.x + e.b.x) / 2, z: (e.a.z + e.b.z) / 2 });

/**
 * FINE NEAR, COARSE FAR (plan map-display, C; the Focus demo's numbers).
 * Terrain is drawn weaker with its distance from the action (`fadeAt`), per
 * solid and per edge, worked out once per terrain and action - never per
 * frame. The grid, whose lines run the width of the arena, fades through one
 * small radial mask over it alone, to nothing by GRID_FADE_M (battlefield/grid).
 */

/** Each solid's strength, by its ground distance from the action; null with no action. */
function useSolidFades(solids: Solid[], action: Vec3 | null): ReadonlyMap<string, number> | null {
  return useMemo(
    () => (action ? new Map(solids.map((s) => [s.id, fadeAt(s.position, action)])) : null),
    [solids, action],
  );
}

interface GridFade {
  id: string;
  x: number;
  y: number;
  r: number;
}

/** The grid's mask: battlefield/grid's fade circle around the action, in the viewBox; none with no action. */
function useGridFade(at: Vec3 | null, camera: Camera): GridFade | null {
  const id = useId().replace(/:/g, '');
  return useMemo(() => (at ? { id: `fade${id}`, ...gridFadeCircle(at, camera, SCENE_VIEW) } : null), [at, camera, id]);
}

/** The grid's mask: shown at the action, gone by its edge (white is shown, its opacity how much). */
function GridFadeMask({ fade }: { fade: GridFade }) {
  const { id, x, y, r } = fade;
  return (
    <defs data-testid="display-depth-fade">
      <radialGradient id={`${id}-g`} gradientUnits="userSpaceOnUse" cx={x} cy={y} r={r}>
        <stop offset="0" stopColor="white" stopOpacity={1} />
        <stop offset="1" stopColor="white" stopOpacity={0} />
      </radialGradient>
      <mask id={id} maskUnits="userSpaceOnUse" x={-1e4} y={-1e4} width={2e4} height={2e4}>
        <rect x={-1e4} y={-1e4} width={2e4} height={2e4} fill={`url(#${id}-g)`} />
      </mask>
    </defs>
  );
}

/** How long the curtain takes to open (the scenes page: 600 ms, from its line). */
const CURTAIN_OPEN_MS = 600;
const lifted = (was: boolean, now: boolean) => was && !now;

/**
 * True for CURTAIN_OPEN_MS after the curtain lifts (Z3): the scene opens from the line it collapsed to. A keyframe,
 * not a transition: the camera's kick rides the same transform, and a transition would smear it.
 */
const useCurtainOpening = (curtained: boolean) => useBriefly(curtained, lifted, CURTAIN_OPEN_MS);

/** The svg's attributes the GPU canvas must share to move with it: every curtain state, and the hit-stop kick. */
function stageAttrs(svgProps: Record<string, unknown>, opening: boolean) {
  const curtain = Object.entries(svgProps).filter(([k]) => k.startsWith('data-curtain'));
  return { ...Object.fromEntries(curtain), 'data-curtain-opening': opening || undefined, style: svgProps.style };
}

/**
 * The GPU map's stage (perf overhaul 2b), for a viewer with it on: one box holding the canvas and the svg over it, so
 * the two are the same size and place by layout, whatever moves around them. Off, the svg stands alone as ever.
 */
function GpuStage({
  gpu,
  camera,
  attrs,
  terrain,
  floor,
  reachStore,
  wedgeStore,
  children,
}: {
  gpu: ReturnType<typeof useMapGpu>;
  camera: Camera;
  attrs: Record<string, unknown>;
  terrain: Record<string, unknown>;
  floor: Record<string, unknown>;
  reachStore: ReachStore;
  wedgeStore: WedgeStore;
  children: ReactNode;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  if (!gpu.on) return children;
  return (
    <div ref={stageRef} className={styles.gpuStage} data-testid="gpu-stage">
      <GpuCanvas
        stageRef={stageRef}
        camera={camera}
        onReady={gpu.ready}
        onUnavailable={gpu.unavailable}
        attrs={attrs}
        terrain={terrain}
        floor={floor}
        reachStore={reachStore}
        wedgeStore={wedgeStore}
      />
      {children}
    </div>
  );
}

/**
 * What the svg hands the layers over the canvas while it draws (displayFaces): the reach, to the canvas's reach layer,
 * and the sight wedges, to the CSS plane that turns them. Publishing re-renders nothing here; null while the svg
 * draws them itself.
 */
function useHandoffs(drawing: boolean) {
  const [reachStore] = useState(makeReachStore);
  const [wedgeStore] = useState(makeWedgeStore);
  return {
    reachStore,
    wedgeStore,
    publishReach: drawing ? reachStore.publish : null,
    publishWedges: drawing ? wedgeStore.publish : null,
  };
}

function DisplaySceneImpl({
  boundary,
  solids,
  camera,
  includeHidden = false,
  showGrid = true,
  selectedSolidId = null,
  voxels = null,
  // The display marks no hazard by hatching: its ground moves instead (zones.ts `surfaceMotion`). Taken here so
  // it stays off the <svg>.
  showTerrainMarks: _marks,
  animation = 'none',
  cutAt = null,
  struckAt = null,
  curtained = false,
  children,
  ...svgProps
}: BattlefieldSceneProps) {
  const { rise, struck } = useDisplayCut({ cutAt, struckAt, solids, curtained });
  const opening = useCurtainOpening(curtained);
  // The action, from the panel (useDisplayAction): where the scene fades from, whom the x-ray keeps.
  const action = useContext(DisplayActionContext);
  const focusPoint = action?.focusPoint ?? null;
  const gridFade = useGridFade(focusPoint, camera);
  const fades = useSolidFades(solids, focusPoint);
  // The GPU map (perf overhaul 2b): a canvas under the svg, behind a per-viewer switch while it is built.
  const gpu = useMapGpu();
  // The svg's grid mask, only while the svg draws the grid (a mask pointing at nothing hides what it masks).
  const svgFade = gpu.drawing ? null : gridFade;
  const { visible, outline, faces, edges, occluder, veil } = useTerrainFrame(
    solids,
    camera,
    includeHidden,
    gpu.drawing,
  );
  // Never lose the actor: what hides the action is drawn as a wireframe (opaque terrain only, with hysteresis).
  const xray = useXray(action?.xrayFigures ?? null, occluder);
  // The looks ride along (step 7): the arrival the viewer picked, the cut's rises played, the last strike.
  const terrain = { solids: visible, outline, fades, xray, selectedSolidId, focusPoint, animation, rise, struck };
  const floor = { boundary, showGrid, focusPoint };
  const { reachStore, wedgeStore, publishReach, publishWedges } = useHandoffs(gpu.drawing);
  return (
    <GpuStage
      gpu={gpu}
      camera={camera}
      attrs={stageAttrs(svgProps, opening)}
      terrain={terrain}
      floor={floor}
      reachStore={reachStore}
      wedgeStore={wedgeStore}
    >
      <svg
        className={styles.scene}
        viewBox={`0 0 ${SCENE_VIEW.width} ${SCENE_VIEW.height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Tactical battlefield"
        data-renderer="display"
        // The svg's own counts; while the canvas draws the terrain, its frame mirrors them (TerrainLayer).
        data-face-count={gpu.drawing ? undefined : faces.length}
        data-edge-count={gpu.drawing ? undefined : edges.length}
        // Where the camera looks, for a spec to sample as it follows the action (Track C).
        data-yaw={camera.yaw.toFixed(1)}
        data-zoom={camera.zoom.toFixed(2)}
        data-target={`${camera.target.x.toFixed(1)},${camera.target.z.toFixed(1)}`}
        data-fov={camera.fov || undefined}
        // The curtain lifting (Z3): the projection opens from its line, not at once.
        data-curtain-opening={opening || undefined}
        // Over the GPU canvas (2b): see-through once the canvas draws, its ground the canvas's.
        data-gpu={gpu.drawing || undefined}
        {...svgProps}
      >
        {svgFade && <GridFadeMask fade={svgFade} />}
        <SceneFloor
          boundary={boundary}
          camera={camera}
          voxels={voxels}
          showGrid={showGrid}
          classes={{ ground: styles.ground, grid: styles.grid, boundary: styles.boundary }}
          gridMask={svgFade ? `url(#${svgFade.id})` : undefined}
          lines={!gpu.drawing}
        />
        {/* The ambient marks on the floor, under the terrain (T4). */}
        <AmbientFloor ambient={action?.ambient} camera={camera} />
        {/* A cut since this screen loaded folds the old set away and raises the next from the floor (I). */}
        <g key={`rise-${rise}`} data-testid="battlefield-solids" className={rise ? styles.cutRise : undefined}>
          <DisplayFaces
            faces={faces}
            selectedSolidId={selectedSolidId}
            xray={xray}
            fades={fades}
            animation={animation}
          />
          <DisplayEdges edges={edges} camera={camera} action={focusPoint} />
          <SurfaceLabels solids={solids} includeHidden={includeHidden} camera={camera} />
        </g>
        <StrikeFlash struck={struck} edges={edges} camera={camera} />
        <DisplayOccluderContext.Provider value={occluder}>
          <DisplayVeilContext.Provider value={veil}>
            <GpuReachContext.Provider value={publishReach}>
              <GpuWedgesContext.Provider value={publishWedges}>
                <DisplayRiseContext.Provider value={rise}>{children}</DisplayRiseContext.Provider>
              </GpuWedgesContext.Provider>
            </GpuReachContext.Provider>
          </DisplayVeilContext.Provider>
        </DisplayOccluderContext.Provider>
        {/* The frame over everything, figures and beats too: the scanlines and the vignette (T4). */}
        <AmbientFrame ambient={action?.ambient} />
      </svg>
    </GpuStage>
  );
}

export default memo(DisplaySceneImpl);
